import {
  fetchWaterLevelChart,
  type WaterLevelPeriod,
} from '@/remote/dashboard/water-level-chart';
import { QUERY_KEYS } from '@/remote/query-key';
import type { WaterLevelChartData } from '@/types/site';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

/**
 * 수위 차트 데이터를 조회하는 훅
 *
 * 백엔드 API 동작:
 * - daily: 어제 00:00 ~ 오늘 23:59의 시간별 데이터 (총 48개 포인트)
 * - weekly: 지난 7일간의 일별 데이터 (총 7개 포인트)
 * - monthly: 지난 30일간의 일별 데이터 (총 30개 포인트)
 *
 * 데이터가 없는 시간대/날짜는 waterLevel이 null로 반환됩니다.
 */

interface UseSiteWaterLevelOptions {
  /** 쿼리 활성화 여부 */
  enabled?: boolean;
  /** 데이터 stale 시간 (밀리초) */
  staleTime?: number;
  /** 가비지 컬렉션 시간 (밀리초) */
  gcTime?: number;
}

interface UseSiteWaterLevelReturn {
  /** 차트 데이터 배열 (waterLevel은 null일 수 있음) */
  data: WaterLevelChartData[] | undefined;
  /** 초기 로딩 상태 */
  isLoading: boolean;
  /** 에러 객체 */
  error: Error | null;
  /** 에러 상태 */
  isError: boolean;
  /** 패칭 중 상태 (백그라운드 업데이트 포함) */
  isFetching: boolean;
  /** 성공 상태 */
  isSuccess: boolean;
  /** 현재 조회 기간 */
  period: WaterLevelPeriod;
  /** 액션 메서드들 */
  actions: {
    /** 조회 기간 변경 */
    setPeriod: (period: WaterLevelPeriod) => void;
    /** 현재 기간 데이터 다시 가져오기 */
    refetch: () => void;
    /** 현재 기간 캐시 무효화 */
    invalidate: () => void;
    /** 해당 사이트의 모든 기간 캐시 무효화 */
    invalidateAll: () => void;
  };
}

/**
 * 사이트 수위 차트 데이터 조회 훅
 *
 * @param siteId - 조회할 사이트 ID
 * @param initialPeriod - 초기 조회 기간 (기본값: 'daily')
 * @param options - 쿼리 옵션들
 * @returns 차트 데이터, 로딩 상태, 액션 메서드들
 *
 * @example
 * ```tsx
 * const { data, isLoading, period, actions } = useSiteWaterLevel('SITE001', 'weekly');
 *
 * // 기간 변경
 * actions.setPeriod('monthly');
 *
 * // 데이터 처리 (null 값 고려)
 * data?.forEach(item => {
 *   if (item.waterLevel !== null) {
 *     console.log(`${item.time}: ${item.waterLevel}%`);
 *   } else {
 *     console.log(`${item.time}: 데이터 없음`);
 *   }
 * });
 * ```
 */
export const useSiteWaterLevel = (
  siteId: string,
  initialPeriod: WaterLevelPeriod = 'daily',
  options: UseSiteWaterLevelOptions = {},
  selectedDate: string | undefined = undefined
): UseSiteWaterLevelReturn => {
  const queryClient = useQueryClient();
  const [periodState, setPeriodState] =
    useState<WaterLevelPeriod>(initialPeriod);

  const {
    enabled = true,
    staleTime = 1000 * 60, // 🚀 1분 - 수위 데이터의 적절한 캐싱
    gcTime = 1000 * 60 * 5, // 🚀 5분 - 메모리 절약
  } = options;

  const query = useQuery<WaterLevelChartData[]>({
    queryKey: [
      ...QUERY_KEYS.dashboard.waterLevelChart(siteId, periodState),
      selectedDate,
    ],
    queryFn: () => fetchWaterLevelChart(siteId, periodState, selectedDate),
    enabled: enabled && !!siteId,
    staleTime,
    gcTime,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const setPeriod = useCallback((newPeriod: WaterLevelPeriod) => {
    setPeriodState(newPeriod);
  }, []);

  const { refetch } = query;

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.dashboard.waterLevelChart(siteId, periodState),
    });
  }, [queryClient, siteId, periodState]);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['대시보드', '수위차트', siteId],
    });
  }, [queryClient, siteId]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    period: periodState,
    actions: {
      setPeriod,
      refetch,
      invalidate,
      invalidateAll,
    },
  };
};
